import { inject } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { Observable, catchError, map, of, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

/**
 * Ajoute l'authentification et la protection CSRF aux appels de notre API.
 * En cas de session expirée, la requête est rejouée une fois après actualisation du jeton.
 */
export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const authService = inject(AuthService);

  if (!request.url.startsWith(environment.apiUrl) || request.url.includes('/auth/csrf')) {
    return next(request);
  }

  const isChangePasswordCall = request.url.includes('/auth/change-password');
  const isPublicCall =
    request.url.includes('/oauth2/') ||
    request.url.includes('/auth/signup') ||
    request.url.includes('/auth/signin') ||
    request.url.includes('/auth/refresh') ||
    request.url.includes('/auth/forgot-password') ||
    request.url.includes('/auth/reset-password');

  /** Prepare la requête avec le JWT et le jeton CSRF quand ils sont requis. */
  const prepareRequest = (currentRequest: HttpRequest<unknown>): Observable<HttpRequest<unknown>> => {
    const token = authService.getToken();
    const authenticatedRequest = !isPublicCall && token
      ? currentRequest.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : currentRequest;

    if (['GET', 'HEAD', 'OPTIONS'].includes(authenticatedRequest.method)) {
      return of(authenticatedRequest);
    }

    return authService.getCsrfToken().pipe(
      map(csrfToken =>
        authenticatedRequest.clone({
          withCredentials: true,
          setHeaders: { 'X-XSRF-TOKEN': csrfToken },
        })
      )
    );
  };

  /** Envoie la requête préparée sans rejouer le flux de récupération CSRF. */
  const sendOnce = (currentRequest: HttpRequest<unknown>): Observable<HttpEvent<unknown>> =>
    prepareRequest(currentRequest).pipe(switchMap(preparedRequest => next(preparedRequest)));

  /** Envoie la requête avec le JWT courant et gère une éventuelle expiration de session. */
  const dispatch = (currentRequest: HttpRequest<unknown>): Observable<HttpEvent<unknown>> =>
    sendOnce(currentRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== 401 || isChangePasswordCall || isPublicCall) {
          return throwError(() => error);
        }

        return authService.refresh().pipe(
          switchMap(() => sendOnce(currentRequest)),
          catchError(refreshError => {
            authService.handleRefreshError();
            return throwError(() => refreshError);
          })
        );
      })
    );

  return dispatch(request);
};
